import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Grid,
  Typography,
  Box,
  Button,
  TextField,
  Card,
  CardContent,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Paper,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import CloseIcon from "@mui/icons-material/Close";
import ExcelJS from "exceljs";
import { getUnitById } from "../Services/unitsService";
import {
  submitEnhancedReservation,
  uploadReservationFiles,
} from "../Services/reservationService";
import { getBrokers } from "../Services/brokerService";
import UnitCard from "../Components/Elements/UnitCard/UnitCard";
import PaymentDetailsForm from "../Components/Elements/PaymentDetailsForm/PaymentDetailsForm";
import PersonalInfoOwnershipForm from "../Components/Elements/PersonalInfoOwnershipForm/PersonalInfoOwnershipForm";
import PDFViewer from "../Components/PDF/PDFViewer";
import { ImageBaseUrl, config } from "../Services/config";
import banksData from "../data/banks.json";

const calculatePaymentTerms = (unit, years, packageType = "silver") => {
  if (!unit)
    return {
      price: 0,
      downPayment: 0,
      quarterlyInstallment: 0,
      tenorMonths: 0,
      totalInstallments: 0,
      downPaymentPercent: 0,
    };
  // Use GoldPrice for Golden, Price for Silver
  const price =
    packageType === "golden"
      ? unit.GoldPrice || unit.Price || 0
      : unit.Price || 0;
  let downPaymentPercent = 0.1; // Default 10% for other years
  if (years === 6) downPaymentPercent = 0.1; // 10%
  if (years === 7) downPaymentPercent = 0.15; // 15%
  if (years === 8) downPaymentPercent = 0.2; // 20%
  if (years === 10) downPaymentPercent = 0.3; // 30%
  const downPayment = price * downPaymentPercent;
  const tenorMonths = years * 12;
  const totalInstallments = years * 4; // Quarterly
  const quarterlyInstallment = (price - downPayment) / totalInstallments;
  return {
    price,
    downPayment,
    quarterlyInstallment,
    tenorMonths,
    totalInstallments,
    downPaymentPercent,
  };
};

function addMonths(date, months) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

const UnitPayment = () => {
  const { unitId, view } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = location.state?.from || '/qr';
  const [unit, setUnit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [brokers, setBrokers] = useState([]);
  const [brokersLoading, setBrokersLoading] = useState(false);
  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [installmentYears, setInstallmentYears] = useState(6);
  const [excelFile, setExcelFile] = useState(null);
  const [excelInstallments, setExcelInstallments] = useState([]);
  const [excelParsingError, setExcelParsingError] = useState("");
  const [editableInstallments, setEditableInstallments] = useState([]);
  const [maintenanceDepositInstallments, setMaintenanceDepositInstallments] =
    useState([]);
  const [editableMaintenanceInstallments, setEditableMaintenanceInstallments] =
    useState([]);
  const [formData, setFormData] = useState({
    transactionType: "",
    feedback: "",
    // NPM/Najma environment only - package type and payment method for first form
    packageType: "", // Will be set based on unit type; clinic/office can be golden or silver
    paymentMethod: config.isNpm || config.isNajma ? "" : "installment", // Default to installment for non-NPM/Najma
  });
  const [firstPaymentDate, setFirstPaymentDate] = useState("");
  const [showNextForm, setShowNextForm] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [uploadFailed, setUploadFailed] = useState(false);
  const [pendingReservationId, setPendingReservationId] = useState(null);
  const [pendingFiles, setPendingFiles] = useState(null);
  const fileInputRefs = {
    idFront: useRef(),
    idBack: useRef(),
    other: useRef(),
  };
  const [nextForm, setNextForm] = useState({
    name: "",
    sex: "",
    address: "",
    job: "",
    phoneNumber: "",
    email: "",
    idNumber: "",
    reservationFee: "",
    primaryOwnerPercentage: "100", // ownership % for primary (default 100%; minus co-owner shares when added)
    company: "",
    brokerId: "",
    directManager: "",
    developerSales: "",
    developerCompany: "",
    developerTeamLeader: "",
    brokerTeamLeader: "",
    idFront: null,
    idBack: null,
    other: [], // Changed to array for multiple files
    // NPM/Najma environment only fields
    nationality: "Egypt",
    // Bank account details (optional, for cash payments)
    bankAccount: "",
    bankName: "",
    bankBranch: "",
    swiftCode: "",
  });

  // Co-owners for multi-user reservation (each with name, email, phone, percentage). Primary + co-owners % must sum to 100%.
  const [coOwners, setCoOwners] = useState([]);

  const [formErrors, setFormErrors] = useState({
    name: "",
    sex: "",
    address: "",
    job: "",
    phoneNumber: "",
    email: "",
    idNumber: "",
    transactionType: "",
    firstPaymentDate: "",
    ownershipPercentages: "",
    coOwnerEmails: "", // "Co-owner emails must be unique / different from main"
    // NPM/Najma environment only errors
    nationality: "",
    packageType: "",
    paymentMethod: "",
  });

  // Per–co-owner field errors (same validation as main form)
  const [coOwnerErrors, setCoOwnerErrors] = useState({});

  useEffect(() => {
    setLoading(true);
    getUnitById(unitId)
      .then((res) => {
        const unitData = typeof res === 'string' ? JSON.parse(res) : res;
        setUnit(unitData);
      })
      .finally(() => setLoading(false));
  }, [unitId]);

  useEffect(() => {
    const fetchBrokers = async () => {
      setBrokersLoading(true);
      try {
        const response = await getBrokers();
        console.log(response);
        const brokerData = response;
        setBrokers(Array.isArray(brokerData) ? brokerData : []);
      } catch (error) {
        console.error("Failed to fetch brokers:", error);
        setSnackbar({
          open: true,
          message: "Failed to load brokers. Please try again later.",
          severity: "warning",
        });
      } finally {
        setBrokersLoading(false);
      }
    };

    fetchBrokers();
  }, []);

  // Fetch banks from JSON file or public API
  const fetchBanks = async (searchQuery = "") => {
    if (!searchQuery || searchQuery.length < 2) {
      setBanks([]);
      return;
    }

    setBanksLoading(true);
    try {
      // Load banks from JSON file (comprehensive global banks list)
      const allBanks = Array.isArray(banksData) ? banksData : [];

      // Filter banks based on search query (case-insensitive search)
      const filteredBanks = allBanks.filter(
        (bank) =>
          typeof bank === "string" &&
          bank.toLowerCase().includes(searchQuery.toLowerCase()),
      );

      // Optional: Try to fetch from a public API to supplement the local list
      // Uncomment and configure if you want to use an external API:
      /*
      try {
        // Example: Using Bank Data API from APILayer (requires API key)
        // const apiKey = 'YOUR_API_KEY_HERE';
        // const response = await fetch(
        //   `https://api.apilayer.com/bank_data/banks_by_country?country_iso=EG`,
        //   { headers: { 'apikey': apiKey } }
        // );
        // if (response.ok) {
        //   const apiData = await response.json();
        //   const apiBanks = (apiData.banks || []).map(bank => bank.name);
        //   filteredBanks.push(...apiBanks);
        // }
      } catch (apiError) {
        console.warn('Bank API not available, using local list:', apiError);
      }
      */

      // Remove duplicates, sort, and limit results for performance (show top 50 matches)
      const uniqueBanks = [...new Set(filteredBanks)].sort().slice(0, 50);

      setBanks(uniqueBanks.map((name) => ({ name })));
    } catch (error) {
      console.error("Failed to fetch banks:", error);
      // Fallback: allow manual entry
      setBanks([]);
    } finally {
      setBanksLoading(false);
    }
  };

  // Calculate payment terms - use Excel values if available, otherwise calculate
  const basePaymentTerms = calculatePaymentTerms(
    unit,
    installmentYears,
    formData.packageType || "silver",
  );

  // If we have Excel installments, use actual values from Excel
  let paymentTerms = basePaymentTerms;
  if (editableInstallments.length > 0 && excelFile) {
    // Find down payment (first installment or one with "down" in name)
    const downPaymentInst =
      editableInstallments.find((inst) =>
        inst.number.toLowerCase().includes("down"),
      ) || editableInstallments[0];

    // Find first quarterly installment (Q1 or first non-down payment)
    const quarterlyInst =
      editableInstallments.find(
        (inst) =>
          !inst.number.toLowerCase().includes("down") &&
          (inst.number.toLowerCase().includes("q1") ||
            inst.number.toLowerCase().includes("installment")),
      ) ||
      editableInstallments.find(
        (inst) => !inst.number.toLowerCase().includes("down"),
      ) ||
      editableInstallments[1];

    if (downPaymentInst) {
      const downPaymentAmount =
        downPaymentInst.rawAmount !== undefined
          ? downPaymentInst.rawAmount
          : parseFloat(
              downPaymentInst.amount?.toString().replace(/[^\d.-]/g, "") || "0",
            ) || 0;

      const quarterlyAmount = quarterlyInst
        ? quarterlyInst.rawAmount !== undefined
          ? quarterlyInst.rawAmount
          : parseFloat(
              quarterlyInst.amount?.toString().replace(/[^\d.-]/g, "") || "0",
            ) || 0
        : basePaymentTerms.quarterlyInstallment;

      // Calculate total price from down payment and installments
      const totalInstallmentsCount = editableInstallments.filter(
        (inst) => !inst.number.toLowerCase().includes("down"),
      ).length;
      const totalPrice =
        downPaymentAmount + quarterlyAmount * totalInstallmentsCount;

      paymentTerms = {
        ...basePaymentTerms,
        price: totalPrice,
        downPayment: downPaymentAmount,
        quarterlyInstallment: quarterlyAmount,
        totalInstallments: totalInstallmentsCount,
        tenorMonths: totalInstallmentsCount * 3, // 3 months per installment
        downPaymentPercent:
          totalPrice > 0
            ? downPaymentAmount / totalPrice
            : basePaymentTerms.downPaymentPercent,
      };

      console.log("Using Excel payment terms:", {
        downPayment: downPaymentAmount,
        quarterlyInstallment: quarterlyAmount,
        totalPrice: totalPrice,
        totalInstallments: totalInstallmentsCount,
      });
    }
  }
  const selectedBroker = brokers.find(
    (broker) => String(broker.Id) === String(nextForm.brokerId),
  );

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^\d{6,15}$/;
    return phoneRegex.test(phone.replace(/\s+/g, ""));
  };

  const validateField = (name, value, nationality = null) => {
    let error = "";

    // Use provided nationality or fall back to nextForm.nationality
    const userNationality =
      nationality !== null ? nationality : nextForm.nationality;

    switch (name) {
      case "name":
        if (!value.trim()) error = "Full name is required";
        else if (value.trim().length < 2)
          error = "Name must be at least 2 characters";
        break;
      case "sex":
        if (!value) error = "Gender is required";
        break;
      case "address":
        // Optional; if provided, min length
        if (value.trim() && value.trim().length < 5)
          error = "Address must be at least 5 characters";
        break;
      case "job":
        // Optional - no validation
        break;
      case "phoneNumber":
        if (!value.trim()) error = "Phone number is required";
        else if (!validatePhoneNumber(value))
          error = "Please enter a valid phone number (6-15 digits)";
        break;
      case "email":
        if (!value.trim()) error = "Email address is required";
        else if (!validateEmail(value))
          error = "Please enter a valid email address";
        break;
      case "idNumber":
        if (!value.trim()) error = "ID number is required";
        else {
          // If user is Egyptian, ID number must be exactly 14 digits
          if (
            (config.isNpm || config.isNajma) &&
            (userNationality?.toLowerCase() === "egypt" || userNationality?.toLowerCase() === "egyptian")
          ) {
            const idNumberDigits = value.trim().replace(/\D/g, ""); // Remove non-digit characters
            if (idNumberDigits.length !== 14) {
              error = "ID number must be exactly 14 digits for Egyptian users";
            }
          } else if (value.trim().length < 5) {
            error = "ID number must be at least 5 characters";
          }
        }
        break;
      // case 'transactionType':
      //   if (!value) error = 'Transaction type is required';
      //   break;
      case "firstPaymentDate":
        // Not required - no validation
        break;
      case "nationality":
        if (!value) error = "Nationality is required";
        break;
      case "paymentMethod":
        if (!value) error = "Payment method is required";
        break;
      case "packageType":
        if (!value) error = "Package type is required";
        break;
      default:
        break;
    }

    return error;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Validate and update errors
    const error = validateField(name, value);
    setFormErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleNextFormChange = (e) => {
    const { name, value, files } = e.target;

    // Handle file inputs
    if (files) {
      // For 'other' field, allow multiple files
      if (name === "other") {
        const fileArray = Array.from(files);
        setNextForm((prev) => ({
          ...prev,
          [name]: [...(prev[name] || []), ...fileArray], // Append new files to existing ones
        }));
      } else {
        // For other file fields (idFront, idBack), keep single file behavior
        const newValue = files[0];
        setNextForm((prev) => ({
          ...prev,
          [name]: newValue,
        }));
      }
    } else {
      // Handle text inputs
      setNextForm((prev) => {
        const updatedForm = {
          ...prev,
          [name]: value,
        };

        // If nationality changes, re-validate ID number (since validation depends on nationality)
        if (name === "nationality" && prev.idNumber) {
          // Re-validate ID number with new nationality
          const idNumberError = validateField("idNumber", prev.idNumber, value);
          setFormErrors((prevErrors) => ({
            ...prevErrors,
            idNumber: idNumberError,
          }));
        }

        return updatedForm;
      });

      // Validate text fields
      // Pass nationality when validating ID number to ensure correct validation
      const nationalityValue =
        name === "idNumber"
          ? nextForm.nationality
          : name === "nationality"
            ? value
            : null;
      const error = validateField(name, value, nationalityValue);
      setFormErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleClearFile = (fileType, fileIndex = null) => {
    if (fileType === "other" && fileIndex !== null) {
      // Remove specific file from array
      setNextForm((prev) => ({
        ...prev,
        [fileType]: prev[fileType].filter((_, index) => index !== fileIndex),
      }));
    } else {
      // Clear entire field (for single file fields or clear all for 'other')
      setNextForm((prev) => ({
        ...prev,
        [fileType]: fileType === "other" ? [] : null,
      }));
      if (fileInputRefs[fileType].current) {
        fileInputRefs[fileType].current.value = "";
      }
    }
  };

  // Multi-user ownership: total % must be 100%
  const getTotalOwnershipPercentage = () => {
    const primary = parseFloat(nextForm.primaryOwnerPercentage) || 0;
    const coTotal = coOwners.reduce(
      (sum, o) => sum + (parseFloat(o.percentage) || 0),
      0,
    );
    return primary + coTotal;
  };

  const addCoOwner = () => {
    setCoOwners((prev) => [
      ...prev,
      {
        id: `co-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        name: "",
        sex: "",
        address: "",
        job: "",
        email: "",
        phoneNumber: "",
        idNumber: "",
        nationality: "",
        percentage: "",
      },
    ]);
  };

  // API: sex must be "Male" or "Female"
  const toApiSex = (v) =>
    v === "male" || v === "female"
      ? v.charAt(0).toUpperCase() + v.slice(1)
      : v || null;

  const removeCoOwner = (id) => {
    setCoOwners((prev) => {
      const next = prev.filter((o) => o.id !== id);
      // Recompute primary share: 100% when no co-owners, else 100 - sum(remaining co-owners)
      const coTotal = next.reduce(
        (sum, o) => sum + (parseFloat(o.percentage) || 0),
        0,
      );
      const primaryRemainder =
        next.length > 0
          ? Math.max(0, Math.min(100, Math.round((100 - coTotal) * 10) / 10))
          : 100;
      setNextForm((f) => ({
        ...f,
        primaryOwnerPercentage: String(primaryRemainder),
      }));
      return next;
    });
    setCoOwnerErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const updateCoOwner = (id, field, value) => {
    setCoOwners((prev) => {
      const next = prev.map((o) =>
        o.id === id ? { ...o, [field]: value } : o,
      );
      // When a co-owner's share changes, set primary owner share to (100 - total co-owner shares)
      if (field === "percentage") {
        const coTotal = next.reduce(
          (sum, o) => sum + (parseFloat(o.percentage) || 0),
          0,
        );
        const primaryRemainder = Math.max(
          0,
          Math.min(100, Math.round((100 - coTotal) * 10) / 10),
        );
        setNextForm((f) => ({
          ...f,
          primaryOwnerPercentage:
            next.length > 0 ? String(primaryRemainder) : "100",
        }));
      }
      return next;
    });
    // Validate this field (same rules as main form); for idNumber use co-owner's nationality
    const co = coOwners.find((o) => o.id === id);
    const nationalityForValidation =
      field === "idNumber"
        ? co?.nationality
        : field === "nationality"
          ? value
          : null;
    const error = validateField(field, value, nationalityForValidation);
    setCoOwnerErrors((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: error },
    }));
  };

  // Generate installment schedule - only for installment payment method
  // Use Excel installments if available, otherwise calculate from years
  const shouldShowInstallments =
    !(config.isNpm || config.isNajma) ||
    formData.paymentMethod === "installment";

  // Function to recalculate installment dates based on down payment date
  // This ensures all dates are calculated from the down payment date
  const recalculateInstallmentDates = (installments, baseDate) => {
    if (!baseDate || installments.length === 0) return installments;

    const downPaymentDate = new Date(baseDate);
    if (isNaN(downPaymentDate.getTime())) return installments;

    let downPaymentIndex = -1;
    // Find the down payment installment index
    installments.forEach((inst, idx) => {
      if (inst.number.toLowerCase().includes("down")) {
        downPaymentIndex = idx;
      }
    });

    // If no down payment found, assume first installment is down payment
    if (downPaymentIndex === -1) {
      downPaymentIndex = 0;
    }

    return installments.map((inst, index) => {
      const isDownPayment =
        inst.number.toLowerCase().includes("down") ||
        index === downPaymentIndex;

      if (isDownPayment) {
        // Update down payment date
        const year = downPaymentDate.getFullYear();
        const month = String(downPaymentDate.getMonth() + 1).padStart(2, "0");
        const day = String(downPaymentDate.getDate()).padStart(2, "0");
        return {
          ...inst,
          dueDate: downPaymentDate.toLocaleDateString(),
          rawDate: `${year}-${month}-${day}`,
        };
      }

      // Calculate quarterly installments (3 months apart from down payment)
      // Count how many installments after the down payment
      const installmentsAfterDownPayment = index - downPaymentIndex;
      const monthsToAdd = installmentsAfterDownPayment * 3;
      const calculatedDate = addMonths(downPaymentDate, monthsToAdd);
      const year = calculatedDate.getFullYear();
      const month = String(calculatedDate.getMonth() + 1).padStart(2, "0");
      const day = String(calculatedDate.getDate()).padStart(2, "0");

      return {
        ...inst,
        dueDate: calculatedDate.toLocaleDateString(),
        rawDate: `${year}-${month}-${day}`,
      };
    });
  };

  // Initialize or update editable installments when conditions change
  useEffect(() => {
    if (shouldShowInstallments) {
      let newInstallments = [];

      if (excelInstallments.length > 0) {
        // Use installments from Excel file
        // Filter out any maintenance-related entries that might have slipped through
        newInstallments = excelInstallments
          .filter((inst) => {
            const instLower = (inst.number || "").toLowerCase();
            return (
              !instLower.includes("maintenance") &&
              !instLower.includes("deposit") &&
              !instLower.includes("total") &&
              !instLower.match(/^[\d.]+%?$/)
            );
          })
          .map((inst) => ({
            ...inst,
            rawAmount:
              inst.rawAmount !== undefined
                ? inst.rawAmount
                : parseFloat(
                    inst.amount?.toString().replace(/[^\d.-]/g, "") || "0",
                  ) || 0,
            rawDate: inst.rawDate || inst.dueDate,
          }));

        // If we have a first payment date, recalculate dates from it
        if (firstPaymentDate && newInstallments.length > 0) {
          newInstallments = recalculateInstallmentDates(
            newInstallments,
            firstPaymentDate,
          );
        }
      } else if (firstPaymentDate && paymentTerms.totalInstallments > 0) {
        // Calculate installments from years
        const downPaymentDate = new Date(firstPaymentDate);
        // Single down payment
        newInstallments.push({
          id: 1,
          number: "Down Payment",
          dueDate: downPaymentDate.toLocaleDateString(),
          amount: paymentTerms.downPayment.toLocaleString(undefined, {
            maximumFractionDigits: 2,
          }),
          rawAmount: paymentTerms.downPayment,
          rawDate: downPaymentDate.toISOString().split("T")[0],
        });
        // Quarterly installments start 3 months after down payment
        for (let i = 0; i < paymentTerms.totalInstallments; i++) {
          const dueDate = addMonths(downPaymentDate, (i + 1) * 3);
          newInstallments.push({
            id: i + 2,
            number: `Installment ${i + 1}`,
            dueDate: dueDate.toLocaleDateString(),
            amount: paymentTerms.quarterlyInstallment.toLocaleString(
              undefined,
              { maximumFractionDigits: 2 },
            ),
            rawAmount: paymentTerms.quarterlyInstallment,
            rawDate: dueDate.toISOString().split("T")[0],
          });
        }
      }

      // Update installments when source data changes
      if (newInstallments.length > 0) {
        // Check if we need to update (different length or Excel file changed)
        const shouldUpdate =
          editableInstallments.length === 0 ||
          editableInstallments.length !== newInstallments.length ||
          (excelInstallments.length > 0 &&
            editableInstallments[0]?.id !== newInstallments[0]?.id);

        if (shouldUpdate) {
          setEditableInstallments(newInstallments);
        } else if (firstPaymentDate && editableInstallments.length > 0) {
          // If only the date changed, recalculate dates for existing installments
          const recalculated = recalculateInstallmentDates(
            editableInstallments,
            firstPaymentDate,
          );
          // Only update if dates actually changed
          const datesChanged = recalculated.some(
            (inst, idx) => inst.rawDate !== editableInstallments[idx]?.rawDate,
          );
          if (datesChanged) {
            setEditableInstallments(recalculated);
          }
        }
      } else {
        setEditableInstallments([]);
      }
    } else {
      setEditableInstallments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    excelInstallments.length,
    firstPaymentDate,
    paymentTerms.totalInstallments,
    installmentYears,
    shouldShowInstallments,
    formData.paymentMethod,
  ]);

  // Use editable installments for display
  const installments = editableInstallments;

  const handleFirstPaymentDateChange = (date) => {
    setFirstPaymentDate(date);
    const error = validateField("firstPaymentDate", date);
    setFormErrors((prev) => ({ ...prev, firstPaymentDate: error }));

    // Recalculate all installment dates when down payment date changes
    if (date && editableInstallments.length > 0) {
      const recalculated = recalculateInstallmentDates(
        editableInstallments,
        date,
      );
      setEditableInstallments(recalculated);
    }
  };

  // Handle installment updates from editable table
  const handleInstallmentUpdate = (updatedInstallments) => {
    setEditableInstallments(updatedInstallments);
  };

  // Handle maintenance deposit installment updates
  const handleMaintenanceInstallmentUpdate = (updatedInstallments) => {
    setEditableMaintenanceInstallments(updatedInstallments);
  };

  // Initialize editable maintenance installments
  useEffect(() => {
    console.log(
      "Maintenance deposit installments useEffect triggered:",
      maintenanceDepositInstallments.length,
    );
    if (maintenanceDepositInstallments.length > 0) {
      const formatted = maintenanceDepositInstallments.map((inst) => ({
        ...inst,
        rawAmount:
          inst.rawAmount !== undefined
            ? inst.rawAmount
            : parseFloat(
                inst.amount?.toString().replace(/[^\d.-]/g, "") || "0",
              ) || 0,
        rawDate: inst.rawDate || inst.dueDate,
      }));
      console.log(
        "Setting editable maintenance installments:",
        formatted.length,
      );
      setEditableMaintenanceInstallments(formatted);
    } else {
      setEditableMaintenanceInstallments([]);
    }
  }, [maintenanceDepositInstallments]);

  // Helper function to parse Excel date value
  const parseExcelDate = (dateValue) => {
    if (!dateValue) return null;

    if (dateValue instanceof Date) {
      return dateValue;
    }

    if (typeof dateValue === "number") {
      // Excel date serial number (days since 1900-01-01)
      const excelEpoch = new Date(1899, 11, 30); // December 30, 1899
      const jsDate = new Date(excelEpoch.getTime() + dateValue * 86400 * 1000);
      if (!isNaN(jsDate.getTime())) {
        return jsDate;
      }
      // Fallback: try standard Excel date conversion
      const excelDate = new Date((dateValue - 25569) * 86400 * 1000);
      return isNaN(excelDate.getTime()) ? null : excelDate;
    }

    // Try to parse as string date (DD/MM/YYYY or other formats)
    const dateStr = dateValue.toString().trim();
    // Try DD/MM/YYYY format first (common in Excel)
    const dateMatch = dateStr.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
    if (dateMatch) {
      const [, day, month, year] = dateMatch;
      const fullYear = year.length === 2 ? `20${year}` : year;
      const dateObj = new Date(
        `${fullYear}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`,
      );
      if (!isNaN(dateObj.getTime())) {
        return dateObj;
      }
    }

    // Try standard date parsing
    const parsedDate = new Date(dateStr);
    return isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  // Parse Excel file to extract installments and dates from the payment plan table
  const handleExcelUpload = async (file) => {
    if (!file) {
      setExcelFile(null);
      setExcelInstallments([]);
      setExcelParsingError("");
      return;
    }

    setExcelFile(file);
    setExcelParsingError("");
    setExcelInstallments([]);

    try {
      const workbook = new ExcelJS.Workbook();
      const arrayBuffer = await file.arrayBuffer();
      await workbook.xlsx.load(arrayBuffer);

      // Try all worksheets, not just the first one
      let worksheet = null;
      let worksheetIndex = 0;

      for (let wsIndex = 0; wsIndex < workbook.worksheets.length; wsIndex++) {
        const ws = workbook.worksheets[wsIndex];
        console.log(
          `Checking worksheet ${wsIndex + 1}: "${ws.name}", rows: ${ws.rowCount}`,
        );
        if (ws.rowCount > 0) {
          worksheet = ws;
          worksheetIndex = wsIndex;
          break;
        }
      }

      if (!worksheet) {
        throw new Error("Excel file is empty or invalid");
      }

      console.log(
        `Using worksheet ${worksheetIndex + 1}: "${worksheet.name}", Total rows: ${worksheet.rowCount}, Total columns: ${worksheet.columnCount}`,
      );

      // Log first 15 rows with all columns to help debug
      console.log("=== First 15 rows of Excel file ===");
      for (let i = 1; i <= Math.min(15, worksheet.rowCount); i++) {
        const row = worksheet.getRow(i);
        const rowData = {};
        for (let j = 1; j <= Math.min(10, row.cellCount); j++) {
          const cell = row.getCell(j);
          const value = cell?.value;
          rowData[`Col${j}`] = {
            value: value,
            type: typeof value,
            string: value?.toString(),
            isDate: value instanceof Date,
            isNumber: typeof value === "number",
          };
        }
        console.log(`Row ${i}:`, rowData);
      }
      console.log("=== End of Excel file preview ===");

      // Helper function to extract text from cell (handles formulas with fallback values)
      const getCellText = (cell) => {
        if (!cell || cell.value === null || cell.value === undefined) return "";

        const value = cell.value;

        // Handle Date objects
        if (value instanceof Date) {
          return value.toLocaleDateString();
        }

        // Handle formula objects (Google Sheets exports formulas with fallback values)
        if (typeof value === "object" && value !== null && value.formula) {
          // Try to extract the fallback value from formula
          // Format: IFERROR(__xludf.DUMMYFUNCTION("..."),"fallback_value")
          const formulaStr = value.formula || "";
          const fallbackMatch = formulaStr.match(/,"([^"]+)"/);
          if (fallbackMatch && fallbackMatch[1]) {
            return fallbackMatch[1].trim();
          }
          // If no fallback, try to get result value
          if (value.result !== undefined) {
            return String(value.result);
          }
        }

        // Handle rich text
        if (typeof value === "object" && value.richText) {
          return value.richText.map((rt) => rt.text || "").join("");
        }

        // Handle regular values
        return String(value).trim();
      };

      // Find the payment plan table by looking for header row with: Year, Installment, %, Due Date, Amount
      // Based on analysis, the header is at row 11 with: Year (col 1), Installment (col 2), % (col 3), Due Date (col 4), Amount (col 5)
      let headerRowIndex = -1;
      let yearColIndex = -1;
      let installmentColIndex = -1;
      let percentColIndex = -1;
      let dueDateColIndex = -1;
      let amountColIndex = -1;

      // Search for header row (check first 50 rows)
      // Also check row 11 directly since we know that's where it is
      for (let i = 1; i <= Math.min(50, worksheet.rowCount); i++) {
        const row = worksheet.getRow(i);
        if (!row) continue;

        let foundYear = false;
        let foundInstallment = false;
        let foundDueDate = false;
        let foundAmount = false;

        for (let j = 1; j <= row.cellCount; j++) {
          const cell = row.getCell(j);
          let cellText = "";
          let cellValue = "";

          // Try multiple methods to get cell text
          try {
            cellText = getCellText(cell);
            cellValue = cellText.toLowerCase().trim();
          } catch (e) {
            // Fallback: try direct value access
            const value = cell?.value;
            if (value) {
              if (typeof value === "object" && value.formula) {
                // Extract from formula string
                const formulaStr = String(value.formula);
                const match = formulaStr.match(/,"([^"]+)"/);
                if (match && match[1]) {
                  cellText = match[1].trim();
                  cellValue = cellText.toLowerCase().trim();
                } else if (value.result !== undefined) {
                  cellText = String(value.result);
                  cellValue = cellText.toLowerCase().trim();
                }
              } else {
                cellText = String(value);
                cellValue = cellText.toLowerCase().trim();
              }
            }
          }

          // More flexible matching for headers
          if (cellValue === "year" || cellValue.includes("year")) {
            yearColIndex = j;
            foundYear = true;
          }
          if (
            cellValue === "installment" ||
            cellValue.includes("installment") ||
            cellValue === "payment" ||
            cellValue.includes("payment")
          ) {
            if (!installmentColIndex) {
              installmentColIndex = j;
              foundInstallment = true;
              console.log(
                `Found Installment column at row ${i}, col ${j}: "${cellText}"`,
              );
            }
          }
          if (
            cellValue === "due date" ||
            cellValue.includes("due date") ||
            (cellValue === "date" && !cellValue.includes("installment"))
          ) {
            if (!dueDateColIndex) {
              dueDateColIndex = j;
              foundDueDate = true;
              console.log(
                `Found Due Date column at row ${i}, col ${j}: "${cellText}"`,
              );
            }
          }
          if (
            cellValue === "amount" ||
            cellValue.includes("amount") ||
            cellValue === "value" ||
            cellValue.includes("value")
          ) {
            if (!amountColIndex) {
              amountColIndex = j;
              foundAmount = true;
              console.log(
                `Found Amount column at row ${i}, col ${j}: "${cellText}"`,
              );
            }
          }
          if (
            cellValue === "%" ||
            cellValue === "percent" ||
            cellValue.includes("percent") ||
            cellValue === "percentage"
          ) {
            percentColIndex = j;
          }
        }

        // If we found the key columns (Installment, Due Date, Amount), this is our header row
        if (foundInstallment && foundDueDate && foundAmount) {
          headerRowIndex = i;
          console.log(
            `Found header row at ${i} with columns: Year=${yearColIndex}, Installment=${installmentColIndex}, DueDate=${dueDateColIndex}, Amount=${amountColIndex}`,
          );
          break;
        }
      }

      // If still not found, try row 11 directly (known location from analysis)
      if (headerRowIndex === -1 && worksheet.rowCount >= 11) {
        console.log("Trying row 11 directly (known header location)...");
        const row11 = worksheet.getRow(11);
        if (row11) {
          // Try to extract text from each cell
          let col1Text = "";
          let col2Text = "";
          let col4Text = "";
          let col5Text = "";

          try {
            col1Text = getCellText(row11.getCell(1)).toLowerCase();
            col2Text = getCellText(row11.getCell(2)).toLowerCase();
            col4Text = getCellText(row11.getCell(4)).toLowerCase();
            col5Text = getCellText(row11.getCell(5)).toLowerCase();
          } catch (e) {
            // Fallback: try direct value access
            const cell1 = row11.getCell(1)?.value;
            const cell2 = row11.getCell(2)?.value;
            const cell4 = row11.getCell(4)?.value;
            const cell5 = row11.getCell(5)?.value;

            if (cell1) {
              if (
                typeof cell1 === "object" &&
                cell1 !== null &&
                cell1.formula
              ) {
                const match = String(cell1.formula).match(/,"([^"]+)"/);
                col1Text = match
                  ? match[1].toLowerCase()
                  : String(cell1).toLowerCase();
              } else {
                col1Text = String(cell1).toLowerCase();
              }
            }
            if (cell2) {
              if (
                typeof cell2 === "object" &&
                cell2 !== null &&
                cell2.formula
              ) {
                const match = String(cell2.formula).match(/,"([^"]+)"/);
                col2Text = match
                  ? match[1].toLowerCase()
                  : String(cell2).toLowerCase();
              } else {
                col2Text = String(cell2).toLowerCase();
              }
            }
            if (cell4) {
              if (
                typeof cell4 === "object" &&
                cell4 !== null &&
                cell4.formula
              ) {
                const match = String(cell4.formula).match(/,"([^"]+)"/);
                col4Text = match
                  ? match[1].toLowerCase()
                  : String(cell4).toLowerCase();
              } else {
                col4Text = String(cell4).toLowerCase();
              }
            }
            if (cell5) {
              if (
                typeof cell5 === "object" &&
                cell5 !== null &&
                cell5.formula
              ) {
                const match = String(cell5.formula).match(/,"([^"]+)"/);
                col5Text = match
                  ? match[1].toLowerCase()
                  : String(cell5).toLowerCase();
              } else {
                col5Text = String(cell5).toLowerCase();
              }
            }
          }

          console.log(
            `Row 11 cell values: Col1="${col1Text}", Col2="${col2Text}", Col4="${col4Text}", Col5="${col5Text}"`,
          );

          // Check if this looks like our header row
          const hasYear = col1Text.includes("year");
          const hasInstallment =
            col2Text.includes("installment") || col2Text.includes("payment");
          const hasDate = col4Text.includes("date") || col4Text.includes("due");
          const hasAmount = col5Text.includes("amount");

          if ((hasYear || hasInstallment) && (hasDate || hasAmount)) {
            headerRowIndex = 11;
            yearColIndex = 1;
            installmentColIndex = 2;
            dueDateColIndex = 4;
            amountColIndex = 5;
            percentColIndex = 3;
            console.log("Found header at row 11 using known structure");
          } else {
            // Even if headers don't match exactly, if row 12 has data, assume row 11 is header
            const row12 = worksheet.getRow(12);
            if (row12) {
              const row12Col2 = getCellText(row12.getCell(2)).toLowerCase();
              const row12Col4 = row12.getCell(4)?.value;
              const row12Col5 = row12.getCell(5)?.value;

              if (
                (row12Col2.includes("down") ||
                  row12Col2.includes("payment") ||
                  row12Col2.includes("q1")) &&
                (row12Col4 || row12Col5)
              ) {
                headerRowIndex = 11;
                yearColIndex = 1;
                installmentColIndex = 2;
                dueDateColIndex = 4;
                amountColIndex = 5;
                percentColIndex = 3;
                console.log(
                  "Found header at row 11 by detecting data pattern in row 12",
                );
              }
            }
          }
        }
      }

      // If still not found, try a more lenient search - just look for "Installment" and "Date" or "Amount"
      if (headerRowIndex === -1) {
        console.log(
          "Header not found with strict matching, trying lenient search...",
        );
        for (let i = 1; i <= Math.min(50, worksheet.rowCount); i++) {
          const row = worksheet.getRow(i);
          if (!row) continue;

          for (let j = 1; j <= row.cellCount; j++) {
            const cell = row.getCell(j);
            const cellValue =
              cell?.value?.toString()?.toLowerCase().trim() || "";

            if (
              (cellValue.includes("installment") ||
                cellValue.includes("payment") ||
                cellValue === "down payment" ||
                cellValue === "down") &&
              !installmentColIndex
            ) {
              installmentColIndex = j;
            }
            if (
              (cellValue.includes("date") || cellValue.includes("due")) &&
              !dueDateColIndex
            ) {
              dueDateColIndex = j;
            }
            if (
              (cellValue.includes("amount") || cellValue.includes("value")) &&
              !amountColIndex
            ) {
              amountColIndex = j;
            }
          }

          // If we found at least Installment and one of Date/Amount, use this row
          if (
            installmentColIndex > 0 &&
            (dueDateColIndex > 0 || amountColIndex > 0)
          ) {
            headerRowIndex = i;
            console.log("Found header row (lenient) at:", i, {
              installmentCol: installmentColIndex,
              dueDateCol: dueDateColIndex,
              amountCol: amountColIndex,
            });
            break;
          }
        }
      }

      // If still not found, try to detect by data pattern
      if (
        headerRowIndex === -1 ||
        !installmentColIndex ||
        !dueDateColIndex ||
        !amountColIndex
      ) {
        console.log("Trying to detect table by data pattern...");

        // Try common column positions based on typical Excel structure
        // Structure: Year (col 1) | Installment (col 2) | % (col 3) | Due Date (col 4) | Amount (col 5)
        const possiblePositions = [
          { inst: 2, date: 4, amount: 5 }, // Most common
          { inst: 2, date: 5, amount: 6 },
          { inst: 3, date: 4, amount: 5 },
          { inst: 1, date: 3, amount: 4 },
        ];

        for (const positions of possiblePositions) {
          // Look for a row that has data matching the pattern
          for (let i = 1; i <= Math.min(30, worksheet.rowCount); i++) {
            const row = worksheet.getRow(i);
            if (!row) continue;

            const instCell =
              row.getCell(positions.inst)?.value?.toString()?.toLowerCase() ||
              "";
            const dateCell = row.getCell(positions.date)?.value;
            const amountCell = row.getCell(positions.amount)?.value;

            // Check if this looks like a data row
            // Installment should be "down payment", "q1", "q2", etc. or contain "payment"
            // Date should be a date or number (Excel date serial)
            // Amount should be a number
            const looksLikeInstallment =
              instCell.includes("down") ||
              instCell.includes("payment") ||
              instCell === "q1" ||
              instCell === "q2" ||
              instCell === "q3" ||
              instCell === "q4";

            const looksLikeDate =
              dateCell instanceof Date ||
              (typeof dateCell === "number" && dateCell > 40000) || // Excel date serial
              (typeof dateCell === "string" &&
                /^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}/.test(dateCell));

            const looksLikeAmount =
              typeof amountCell === "number" && amountCell > 0;

            if (looksLikeInstallment && (looksLikeDate || looksLikeAmount)) {
              installmentColIndex = positions.inst;
              dueDateColIndex = positions.date;
              amountColIndex = positions.amount;
              headerRowIndex = i - 1; // Assume previous row is header
              console.log(
                "Found table by pattern at row:",
                i,
                "using positions:",
                positions,
              );
              break;
            }
          }

          if (headerRowIndex > 0) break;
        }

        if (
          headerRowIndex === -1 ||
          !installmentColIndex ||
          !dueDateColIndex ||
          !amountColIndex
        ) {
          // Last resort: try to scan all cells and find any that look like our data
          console.log(
            "=== Last resort: scanning all cells for data patterns ===",
          );
          for (let i = 1; i <= Math.min(20, worksheet.rowCount); i++) {
            const row = worksheet.getRow(i);
            if (!row) continue;

            for (let j = 1; j <= row.cellCount; j++) {
              const cell = row.getCell(j);
              const value = cell?.value;
              const strValue = value?.toString()?.toLowerCase() || "";

              // Check for installment-like values
              if (
                (strValue.includes("down") ||
                  strValue.includes("payment") ||
                  strValue === "q1" ||
                  strValue === "q2" ||
                  strValue === "q3" ||
                  strValue === "q4") &&
                !installmentColIndex
              ) {
                console.log(
                  `Found potential installment at row ${i}, col ${j}: "${value}"`,
                );
                // Check if nearby columns have date/amount
                const dateCell =
                  row.getCell(j + 2)?.value || row.getCell(j + 1)?.value;
                const amountCell =
                  row.getCell(j + 3)?.value || row.getCell(j + 2)?.value;

                if (dateCell || amountCell) {
                  installmentColIndex = j;
                  if (
                    dateCell &&
                    (dateCell instanceof Date ||
                      typeof dateCell === "number" ||
                      /^\d{1,2}[\/\-]/.test(dateCell?.toString()))
                  ) {
                    dueDateColIndex = j + (row.getCell(j + 2)?.value ? 2 : 1);
                  }
                  if (
                    amountCell &&
                    typeof amountCell === "number" &&
                    amountCell > 0
                  ) {
                    amountColIndex = j + (row.getCell(j + 3)?.value ? 3 : 2);
                  }
                  headerRowIndex = i - 1;
                  console.log("Found table structure:", {
                    installmentColIndex,
                    dueDateColIndex,
                    amountColIndex,
                    headerRowIndex,
                  });
                  break;
                }
              }
            }
            if (headerRowIndex > 0) break;
          }
        }

        if (
          headerRowIndex === -1 ||
          !installmentColIndex ||
          !dueDateColIndex ||
          !amountColIndex
        ) {
          console.error("Could not detect table structure. Columns found:", {
            installmentColIndex,
            dueDateColIndex,
            amountColIndex,
            headerRowIndex,
          });
          console.error(
            "Please check the console logs above to see the actual Excel file structure.",
          );
          throw new Error(
            "Could not find payment plan table. Please ensure the Excel file contains a table with columns for Installment, Due Date, and Amount. The table should start with a header row containing these column names. Check the browser console for detailed file structure.",
          );
        }
      }

      // Get down payment date from input or use today
      let baseDate = null;
      if (firstPaymentDate) {
        baseDate = new Date(firstPaymentDate);
      } else {
        baseDate = new Date();
        baseDate.setHours(0, 0, 0, 0);
      }

      const installments = [];
      let installmentId = 1;
      let downPaymentDate = null;

      // Find where maintenance deposit section starts (to stop main installment parsing)
      let maintenanceStartRow = -1;
      for (
        let checkRow = headerRowIndex + 1;
        checkRow <= Math.min(headerRowIndex + 100, worksheet.rowCount);
        checkRow++
      ) {
        const checkRowData = worksheet.getRow(checkRow);
        if (!checkRowData) continue;

        for (let j = 1; j <= checkRowData.cellCount; j++) {
          const cell = checkRowData.getCell(j);
          const cellText = getCellText(cell).toLowerCase();
          if (
            cellText.includes("maintenance") &&
            cellText.includes("deposit")
          ) {
            maintenanceStartRow = checkRow;
            break;
          }
        }
        if (maintenanceStartRow > 0) break;
      }

      // Parse data rows starting from the row after header, but stop before maintenance deposit
      const endRow =
        maintenanceStartRow > 0 ? maintenanceStartRow - 1 : worksheet.rowCount;
      for (let i = headerRowIndex + 1; i <= endRow; i++) {
        const row = worksheet.getRow(i);
        if (!row) continue;

        // Extract values using helper function to handle formulas
        const installmentCell =
          installmentColIndex > 0 ? row.getCell(installmentColIndex) : null;
        const dateCell =
          dueDateColIndex > 0 ? row.getCell(dueDateColIndex) : null;
        const amountCell =
          amountColIndex > 0 ? row.getCell(amountColIndex) : null;

        const installmentValue = installmentCell
          ? getCellText(installmentCell) || installmentCell.value
          : null;
        let dateValue = dateCell ? dateCell.value : null;
        let amountValue = amountCell ? amountCell.value : null;

        // Handle formula cells - extract fallback value or result
        if (
          dateCell &&
          dateCell.value !== null &&
          dateCell.value !== undefined
        ) {
          if (typeof dateCell.value === "object" && dateCell.value.formula) {
            const formulaStr = dateCell.value.formula || "";
            const fallbackMatch = formulaStr.match(/,"([^"]+)"/);
            if (fallbackMatch && fallbackMatch[1]) {
              const fallback = fallbackMatch[1].trim();
              // Check if it's a number (Excel date serial)
              const numValue = parseFloat(fallback);
              if (!isNaN(numValue)) {
                dateValue = numValue;
              } else {
                dateValue = fallback;
              }
            } else if (dateCell.value.result !== undefined) {
              dateValue = dateCell.value.result;
            }
          } else if (typeof dateCell.value === "number") {
            // Direct number value (Excel date serial)
            dateValue = dateCell.value;
          }
        }

        if (
          amountCell &&
          amountCell.value !== null &&
          amountCell.value !== undefined
        ) {
          if (
            typeof amountCell.value === "object" &&
            amountCell.value.formula
          ) {
            const formulaStr = amountCell.value.formula || "";
            const fallbackMatch = formulaStr.match(/,"([^"]+)"/);
            if (fallbackMatch && fallbackMatch[1]) {
              const fallback = fallbackMatch[1].trim();
              const numValue = parseFloat(fallback);
              if (!isNaN(numValue)) {
                amountValue = numValue;
              } else {
                amountValue = fallback;
              }
            } else if (amountCell.value.result !== undefined) {
              amountValue = amountCell.value.result;
            }
          } else if (typeof amountCell.value === "number") {
            // Direct number value
            amountValue = amountCell.value;
          }
        }

        // Skip empty rows
        if (!installmentValue && !dateValue && !amountValue) {
          continue;
        }

        // Parse installment name
        let installmentNumber = "";
        if (installmentValue) {
          const instStr = installmentValue.toString().trim();
          if (
            instStr.toLowerCase().includes("down") ||
            instStr.toLowerCase() === "down payment"
          ) {
            installmentNumber = "Down Payment";
          } else {
            installmentNumber = instStr;
          }
        } else {
          installmentNumber =
            installmentId === 1
              ? "Down Payment"
              : `Installment ${installmentId - 1}`;
        }

        // Parse date - use Excel date if available, otherwise calculate from base date
        let dueDate = null;
        let rawDateStr = "";

        if (dateValue) {
          // Try to parse the date from Excel
          const parsedDate = parseExcelDate(dateValue);
          if (parsedDate) {
            dueDate = parsedDate;
            const year = parsedDate.getFullYear();
            const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
            const day = String(parsedDate.getDate()).padStart(2, "0");
            rawDateStr = `${year}-${month}-${day}`;

            // If this is the down payment, update base date
            if (installmentNumber.toLowerCase().includes("down")) {
              downPaymentDate = parsedDate;
              baseDate = new Date(parsedDate);
            }
          }
        }

        // If no date found and this is the first installment (down payment), use base date
        if (!dueDate && installmentId === 1) {
          dueDate = new Date(baseDate);
          const year = dueDate.getFullYear();
          const month = String(dueDate.getMonth() + 1).padStart(2, "0");
          const day = String(dueDate.getDate()).padStart(2, "0");
          rawDateStr = `${year}-${month}-${day}`;
          downPaymentDate = dueDate;
        }
        // If no date found for subsequent installments, calculate from down payment date
        else if (!dueDate && downPaymentDate) {
          // Calculate quarterly installments (3 months apart)
          const monthsToAdd = (installmentId - 1) * 3;
          dueDate = addMonths(downPaymentDate, monthsToAdd);
          const year = dueDate.getFullYear();
          const month = String(dueDate.getMonth() + 1).padStart(2, "0");
          const day = String(dueDate.getDate()).padStart(2, "0");
          rawDateStr = `${year}-${month}-${day}`;
        }
        // If still no date, use base date with quarterly increments
        else if (!dueDate) {
          const monthsToAdd = (installmentId - 1) * 3;
          dueDate = addMonths(baseDate, monthsToAdd);
          const year = dueDate.getFullYear();
          const month = String(dueDate.getMonth() + 1).padStart(2, "0");
          const day = String(dueDate.getDate()).padStart(2, "0");
          rawDateStr = `${year}-${month}-${day}`;
        }

        // Parse amount
        let amount = 0;
        if (amountValue) {
          if (typeof amountValue === "number") {
            amount = amountValue;
          } else {
            const parsedAmount = parseFloat(
              amountValue.toString().replace(/[^\d.-]/g, ""),
            );
            if (!isNaN(parsedAmount)) {
              amount = parsedAmount;
            }
          }
        }

        // Skip maintenance deposit entries - they will be in a separate table
        const instLower = installmentNumber.toLowerCase();
        if (
          instLower.includes("maintenance") ||
          instLower.includes("deposit") ||
          instLower === "%" ||
          instLower.match(/^[\d.]+%?$/) || // Skip percentage-only rows like "0.1667", "16.67%"
          instLower.includes("total")
        ) {
          continue; // Skip maintenance entries from main installments
        }

        // Only add installment if it has an amount or date
        if (amount > 0 || dueDate) {
          installments.push({
            id: installmentId++,
            number: installmentNumber,
            dueDate: dueDate ? dueDate.toLocaleDateString() : "N/A",
            amount: amount.toLocaleString(undefined, {
              maximumFractionDigits: 2,
            }),
            rawAmount: amount,
            rawDate: rawDateStr,
          });
        }
      }

      if (installments.length === 0) {
        throw new Error(
          "No valid installments found in the payment plan table. Please check the Excel file format.",
        );
      }

      setExcelInstallments(installments);

      // Parse Maintenance Deposit table
      // Based on Excel analysis: Row 57 = "Maintenance Deposit" title, Row 58 = Header, Rows 59-64 = Data
      const maintenanceInstallments = [];
      let maintenanceHeaderRow = -1;
      let maintenanceInstCol = -1;
      let maintenanceDateCol = -1;
      let maintenanceAmountCol = -1;

      // Search for "Maintenance Deposit" - known to be around row 57
      console.log(
        "Searching for Maintenance Deposit header starting from row:",
        headerRowIndex + 1,
      );
      for (
        let i = headerRowIndex + 1;
        i <= Math.min(headerRowIndex + 100, worksheet.rowCount);
        i++
      ) {
        const row = worksheet.getRow(i);
        if (!row) continue;

        // Check if this row contains "Maintenance Deposit"
        for (let j = 1; j <= row.cellCount; j++) {
          const cell = row.getCell(j);
          const cellText = getCellText(cell).toLowerCase();
          if (
            cellText.includes("maintenance") &&
            cellText.includes("deposit")
          ) {
            // Next row should be the header (row 58)
            maintenanceHeaderRow = i + 1;
            console.log(
              "Found Maintenance Deposit at row:",
              i,
              "Header row will be:",
              maintenanceHeaderRow,
            );
            break;
          }
        }

        if (maintenanceHeaderRow > 0) {
          console.log(
            "Processing Maintenance Deposit table starting at row:",
            maintenanceHeaderRow,
          );
          // Find header columns in row 58
          const headerRow = worksheet.getRow(maintenanceHeaderRow);
          if (headerRow) {
            console.log("Maintenance Deposit header row cells:");
            for (let j = 1; j <= headerRow.cellCount; j++) {
              const cell = headerRow.getCell(j);
              const cellText = getCellText(cell).toLowerCase().trim();
              console.log(`  Col ${j}: "${cellText}"`);
              if (cellText.includes("installment") && !maintenanceInstCol) {
                maintenanceInstCol = j;
                console.log(`    -> Found Installment column at ${j}`);
              }
              if (
                (cellText.includes("date") || cellText.includes("due")) &&
                !maintenanceDateCol
              ) {
                maintenanceDateCol = j;
                console.log(`    -> Found Date column at ${j}`);
              }
              if (cellText.includes("amount") && !maintenanceAmountCol) {
                maintenanceAmountCol = j;
                console.log(`    -> Found Amount column at ${j}`);
              }
            }

            // If we found the columns, parse the data (rows 59-64, stop at Total row 65)
            if (
              maintenanceInstCol > 0 &&
              maintenanceDateCol > 0 &&
              maintenanceAmountCol > 0
            ) {
              console.log("Maintenance Deposit columns found:", {
                installment: maintenanceInstCol,
                date: maintenanceDateCol,
                amount: maintenanceAmountCol,
              });
              let maintenanceId = 1;
              // Parse data rows (typically rows 59-64, stop at Total row 65)
              for (
                let k = maintenanceHeaderRow + 1;
                k <= Math.min(maintenanceHeaderRow + 10, worksheet.rowCount);
                k++
              ) {
                const dataRow = worksheet.getRow(k);
                if (!dataRow) continue;

                const instCell = dataRow.getCell(maintenanceInstCol);
                const dateCell = dataRow.getCell(maintenanceDateCol);
                const amountCell = dataRow.getCell(maintenanceAmountCol);

                const instValue = getCellText(instCell).trim();
                let dateValue = dateCell?.value;
                let amountValue = amountCell?.value;

                console.log(
                  `Row ${k} - Installment: "${instValue}", Date: ${dateValue}, Amount: ${amountValue}`,
                );

                // Skip if this is a "Total" row
                if (instValue.toLowerCase().includes("total")) {
                  console.log(
                    "Reached Total row, stopping maintenance deposit parsing",
                  );
                  break;
                }

                // Skip empty rows
                if (!instValue && !dateValue && !amountValue) {
                  continue;
                }

                // Handle formula cells for maintenance
                if (
                  dateCell &&
                  dateCell.value !== null &&
                  dateCell.value !== undefined
                ) {
                  if (
                    typeof dateCell.value === "object" &&
                    dateCell.value.formula
                  ) {
                    const formulaStr = dateCell.value.formula || "";
                    const fallbackMatch = formulaStr.match(/,"([^"]+)"/);
                    if (fallbackMatch && fallbackMatch[1]) {
                      const fallback = fallbackMatch[1].trim();
                      const numValue = parseFloat(fallback);
                      if (!isNaN(numValue)) {
                        dateValue = numValue;
                      } else {
                        dateValue = fallback;
                      }
                    } else if (dateCell.value.result !== undefined) {
                      dateValue = dateCell.value.result;
                    }
                  } else if (typeof dateCell.value === "number") {
                    dateValue = dateCell.value;
                  }
                }

                if (
                  amountCell &&
                  amountCell.value !== null &&
                  amountCell.value !== undefined
                ) {
                  if (
                    typeof amountCell.value === "object" &&
                    amountCell.value.formula
                  ) {
                    const formulaStr = amountCell.value.formula || "";
                    const fallbackMatch = formulaStr.match(/,"([^"]+)"/);
                    if (fallbackMatch && fallbackMatch[1]) {
                      const fallback = fallbackMatch[1].trim();
                      const numValue = parseFloat(fallback);
                      if (!isNaN(numValue)) {
                        amountValue = numValue;
                      } else {
                        amountValue = fallback;
                      }
                    } else if (amountCell.value.result !== undefined) {
                      amountValue = amountCell.value.result;
                    }
                  } else if (typeof amountCell.value === "number") {
                    amountValue = amountCell.value;
                  }
                }

                // Skip empty rows
                if (!instValue && !dateValue && !amountValue) {
                  continue;
                }

                // Parse date
                let dueDate = null;
                let rawDateStr = "";
                if (dateValue) {
                  const parsedDate = parseExcelDate(dateValue);
                  if (parsedDate) {
                    dueDate = parsedDate;
                    const year = parsedDate.getFullYear();
                    const month = String(parsedDate.getMonth() + 1).padStart(
                      2,
                      "0",
                    );
                    const day = String(parsedDate.getDate()).padStart(2, "0");
                    rawDateStr = `${year}-${month}-${day}`;
                  }
                }

                // Parse amount
                let amount = 0;
                if (amountValue) {
                  if (typeof amountValue === "number") {
                    amount = amountValue;
                  } else {
                    const parsedAmount = parseFloat(
                      amountValue.toString().replace(/[^\d.-]/g, ""),
                    );
                    if (!isNaN(parsedAmount)) {
                      amount = parsedAmount;
                    }
                  }
                }

                // Skip "Total" rows
                if (instValue.toLowerCase().includes("total")) {
                  break;
                }

                // Skip percentage-only rows (like "16.67%", "0.1667")
                if (instValue && instValue.match(/^[\d.]+%?$/)) {
                  continue;
                }

                // Only add if it has meaningful data
                // Must have either a proper installment name, or amount > 0, or a valid date
                const hasValidName =
                  instValue &&
                  !instValue.match(/^[\d.]+%?$/) &&
                  !instValue.toLowerCase().includes("total") &&
                  instValue.trim().length > 0;

                if (hasValidName || amount > 0 || dueDate) {
                  // Format installment number properly
                  let formattedNumber = instValue.trim();
                  if (!formattedNumber || formattedNumber.match(/^[\d.]+%?$/)) {
                    formattedNumber = `Maintenance Installment ${maintenanceId}`;
                  }

                  // Only add if amount > 0 or has a valid date
                  if (amount > 0 || dueDate) {
                    maintenanceInstallments.push({
                      id: maintenanceId++,
                      number: formattedNumber,
                      dueDate: dueDate ? dueDate.toLocaleDateString() : "N/A",
                      amount: amount.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      }),
                      rawAmount: amount,
                      rawDate: rawDateStr,
                    });
                    console.log(
                      "Added maintenance installment:",
                      formattedNumber,
                      amount,
                      rawDateStr,
                    );
                  }
                }
              }
              console.log(
                "Total maintenance installments found:",
                maintenanceInstallments.length,
              );
              break;
            } else {
              console.log("Maintenance Deposit columns not found. Found:", {
                installment: maintenanceInstCol,
                date: maintenanceDateCol,
                amount: maintenanceAmountCol,
              });
            }
          }
          break;
        }
      }

      if (maintenanceInstallments.length === 0) {
        console.log(
          "No maintenance installments found. Searched up to row:",
          Math.min(headerRowIndex + 100, worksheet.rowCount),
        );
      }

      setMaintenanceDepositInstallments(maintenanceInstallments);
      console.log(
        "Maintenance deposit installments parsed:",
        maintenanceInstallments.length,
        maintenanceInstallments,
      );

      // Set first payment date from down payment if available
      if (downPaymentDate) {
        const year = downPaymentDate.getFullYear();
        const month = String(downPaymentDate.getMonth() + 1).padStart(2, "0");
        const day = String(downPaymentDate.getDate()).padStart(2, "0");
        setFirstPaymentDate(`${year}-${month}-${day}`);
      } else if (installments.length > 0 && installments[0].rawDate) {
        setFirstPaymentDate(installments[0].rawDate);
      }

      const totalInstallments = installments.length;
      const maintenanceCount = maintenanceInstallments.length;
      const message =
        maintenanceCount > 0
          ? `Successfully loaded ${totalInstallments} installments and ${maintenanceCount} maintenance deposit installments from Excel file!`
          : `Successfully loaded ${totalInstallments} installments from Excel file!`;

      setSnackbar({
        open: true,
        message: message,
        severity: "success",
      });
    } catch (error) {
      console.error("Error parsing Excel file:", error);
      setExcelParsingError(error.message || "Failed to parse Excel file");
      setExcelFile(null);
      setExcelInstallments([]);
      setSnackbar({
        open: true,
        message: `Error parsing Excel file: ${error.message || "Invalid file format"}`,
        severity: "error",
      });
    }
  };

  const validateAllFields = () => {
    const errors = {};

    // Validate first form fields
    errors.transactionType = validateField(
      "transactionType",
      formData.transactionType,
    );

    errors.packageType = validateField("packageType", formData.packageType);
    if (config.isNpm || config.isNajma) {
      errors.paymentMethod = validateField("paymentMethod", formData.paymentMethod);
    }

    // Validate second form fields
    errors.name = validateField("name", nextForm.name);
    errors.sex = validateField("sex", nextForm.sex);
    errors.address = validateField("address", nextForm.address);
    errors.job = validateField("job", nextForm.job);
    errors.phoneNumber = validateField("phoneNumber", nextForm.phoneNumber);
    errors.email = validateField("email", nextForm.email);
    errors.idNumber = validateField("idNumber", nextForm.idNumber);

    // Multi-user ownership: total % must equal 100%
    const totalOwnership = getTotalOwnershipPercentage();
    const hasAnyOwnership =
      (parseFloat(nextForm.primaryOwnerPercentage) || 0) > 0 ||
      coOwners.some((o) => (parseFloat(o.percentage) || 0) > 0);
    if (hasAnyOwnership && Math.abs(totalOwnership - 100) > 0.01) {
      errors.ownershipPercentages = "Ownership must total 100%";
    } else {
      errors.ownershipPercentages = "";
    }
    // API: co-owner emails must be unique and different from main client
    const mainEmail = (nextForm.email || "").trim().toLowerCase();
    const coEmails = coOwners
      .map((o) => (o.email || "").trim().toLowerCase())
      .filter(Boolean);
    const duplicateCo = coEmails.some((e, i) => coEmails.indexOf(e) !== i);
    const sameAsMain = coEmails.some((e) => e === mainEmail);
    if (duplicateCo) {
      errors.coOwnerEmails = "Co-owner emails must be unique.";
    } else if (sameAsMain) {
      errors.coOwnerEmails =
        "Co-owner email cannot be the same as primary owner email.";
    } else {
      errors.coOwnerEmails = "";
    }

    // Validate NPM/Najma environment only fields (packageType and paymentMethod are validated in first form)
    if (config.isNpm || config.isNajma) {
      errors.nationality = validateField("nationality", nextForm.nationality);
      // Package type and payment method are validated in the first form (formData)
      // They're already copied to nextForm when moving to second form
    }

    setFormErrors(errors);

    // Validate co-owners (same fields as main form): only co-owners with any data
    const coOwnerFields = [
      "name",
      "sex",
      "address",
      "job",
      "phoneNumber",
      "email",
      "idNumber",
      "nationality",
    ];
    const nextCoOwnerErrors = {};
    let hasCoOwnerErrors = false;
    coOwners.forEach((co) => {
      const hasAnyData =
        coOwnerFields.some((f) => (co[f] || "").toString().trim()) ||
        (parseFloat(co.percentage) || 0) > 0;
      if (!hasAnyData) return;
      const coErrs = {};
      coOwnerFields.forEach((f) => {
        const err = validateField(f, co[f], f === "idNumber" ? co.nationality : null);
        coErrs[f] = err;
        if (err) hasCoOwnerErrors = true;
      });
      nextCoOwnerErrors[co.id] = coErrs;
    });
    setCoOwnerErrors(nextCoOwnerErrors);

    // Return true if no errors (main form and co-owners)
    return (
      Object.values(errors).every((error) => !error) && !hasCoOwnerErrors
    );
  };

  const resetForm = () => {
    setFormData({
      transactionType: "",
      feedback: "",
      // NPM/Najma environment only - reset package type and payment method
      packageType: "",
      paymentMethod: config.isNpm || config.isNajma ? "" : "installment",
    });
    setFirstPaymentDate("");
    setShowNextForm(false);
    setInstallmentYears(6);
    setExcelFile(null);
    setExcelInstallments([]);
    setExcelParsingError("");
    setEditableInstallments([]);
    setMaintenanceDepositInstallments([]);
    setEditableMaintenanceInstallments([]);
    setNextForm({
      name: "",
      sex: "",
      address: "",
      job: "",
      phoneNumber: "",
      email: "",
      idNumber: "",
      reservationFee: "",
      primaryOwnerPercentage: "100",
      company: "",
      brokerId: "",
      directManager: "",
      developerSales: "",
      developerCompany: "",
      developerTeamLeader: "",
      brokerTeamLeader: "",
      idFront: null,
      idBack: null,
      other: [],
      // NPM/Najma environment only fields
      nationality: "",
      // Bank account details
      bankAccount: "",
      bankName: "",
      bankBranch: "",
      swiftCode: "",
    });
    setFormErrors({
      name: "",
      sex: "",
      address: "",
      job: "",
      phoneNumber: "",
      email: "",
      idNumber: "",
      transactionType: "",
      firstPaymentDate: "",
      // NPM/Najma environment only errors
      nationality: "",
      paymentMethod: "",
      ownershipPercentages: "",
      coOwnerEmails: "",
    });
    setCoOwners([]);
    setCoOwnerErrors({});
    // Clear upload failure state
    setUploadFailed(false);
    setPendingReservationId(null);
    setPendingFiles(null);
    // Clear file inputs
    Object.values(fileInputRefs).forEach((ref) => {
      if (ref.current) {
        ref.current.value = "";
      }
    });
  };

  const handleReuploadFiles = async () => {
    if (loading || !pendingReservationId || !pendingFiles) return;

    setLoading(true);

    try {
      console.log(
        "Reuploading files for reservation ID:",
        pendingReservationId,
      );
      console.log("Files to reupload:", pendingFiles);
      await uploadReservationFiles(pendingReservationId, pendingFiles);
      setSnackbar({
        open: true,
        message: "Files uploaded successfully!",
        severity: "success",
      });
      // Clear upload failure state on success
      setUploadFailed(false);
      setPendingReservationId(null);
      setPendingFiles(null);
      setTimeout(() => {
        navigate(returnPath, { replace: true });
      }, 2000);
    } catch (fileError) {
      console.error("File reupload error:", fileError);
      const errorMessage =
        fileError.response?.data?.message ||
        fileError.message ||
        "Failed to upload files. Please try again.";
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (loading) return;

    // Validate all fields before submission
    if (!validateAllFields()) {
      setSnackbar({
        open: true,
        message: "Please fix the validation errors before submitting.",
        severity: "error",
      });
      return;
    }

    setLoading(true);

    try {
      // API: CoOwners[] — first item = primary, rest = co-owners. OwnershipPercentage per item; sum must be 100.
      const mapOwnerToCoOwner = (owner, ownershipPercentage) => ({
        Name: owner.name?.trim() ?? "",
        Sex: toApiSex(owner.sex) ?? "",
        Job: owner.job?.trim() ?? "",
        PhoneNumber: owner.phoneNumber?.trim() ?? "",
        Email: owner.email?.trim() ?? "",
        IdNumber: owner.idNumber?.trim() ?? "",
        OwnershipPercentage: Math.max(0, Math.min(100, parseFloat(ownershipPercentage) || 0)),
        IsEgyptian: owner.nationality?.toLowerCase() === "egypt" || owner.nationality?.toLowerCase() === "egyptian",
        ...(owner.address ? { Address: owner.address.trim() } : {}),
        ...(owner.nationality ? { Nationality: owner.nationality } : {}),
      });

      const primaryPercentage =
        nextForm.primaryOwnerPercentage !== "" &&
        nextForm.primaryOwnerPercentage != null
          ? parseFloat(nextForm.primaryOwnerPercentage)
          : 0;

      const primaryCoOwner = mapOwnerToCoOwner(nextForm, primaryPercentage);

      const coOwnersList = coOwners
        .filter(
          (o) =>
            (parseFloat(o.percentage) || 0) > 0 &&
            o.name &&
            o.email &&
            o.phoneNumber &&
            o.idNumber &&
            o.sex,
        )
        .map((o) => mapOwnerToCoOwner(o, o.percentage));

      const coOwnersPayload = [primaryCoOwner, ...coOwnersList];

      const reservationData = {
        CoOwners: coOwnersPayload,
        UnitId: String(unitId),
        ...(nextForm.reservationFee
          ? { ReservationFee: parseFloat(nextForm.reservationFee) }
          : {}),
        ...(selectedBroker?.Id != null || nextForm.brokerId
          ? { BrokerId: selectedBroker?.Id ?? nextForm.brokerId ?? null }
          : {}),
        ...(nextForm.directManager
          ? { DirectManager: nextForm.directManager }
          : {}),
        ...(nextForm.developerSales
          ? { DeveloperSales: nextForm.developerSales }
          : {}),
        ...(nextForm.developerCompany
          ? { DeveloperCompany: nextForm.developerCompany }
          : {}),
        ...(nextForm.developerTeamLeader
          ? { DeveloperTeamLeader: nextForm.developerTeamLeader }
          : {}),
        ...(formData.feedback ? { Feedback: formData.feedback } : {}),
        // installmentSchedule: formData.paymentMethod === 'installment' ? editableInstallments.map(inst => {
        //   // Prioritize rawAmount from Excel, fallback to parsing formatted amount
        //   let finalAmount = 0;
        //   if (inst.rawAmount !== undefined && inst.rawAmount !== null) {
        //     finalAmount = inst.rawAmount;
        //   } else if (inst.amount !== undefined && inst.amount !== null) {
        //     if (typeof inst.amount === 'number') {
        //       finalAmount = inst.amount;
        //     } else {
        //       finalAmount = parseFloat(inst.amount.toString().replace(/[^\d.-]/g, '') || '0') || 0;
        //     }
        //   }
        //
        //   console.log('Installment schedule mapping:', {
        //     id: inst.id,
        //     number: inst.number,
        //     rawAmount: inst.rawAmount,
        //     amount: inst.amount,
        //     finalAmount: finalAmount
        //   });
        //
        //   return {
        //     id: inst.id,
        //     number: inst.number,
        //     dueDate: inst.rawDate || inst.dueDate,
        //     amount: finalAmount
        //   };
        // }) : [],
        ...(firstPaymentDate && { FirstPaymentDate: firstPaymentDate }),
        ...(installmentYears && { InstallmentYears: installmentYears }),
        ...(formData.transactionType && {
          TransactionType: formData.transactionType,
        }),
        // Unit installment paymentTerms - commented out so not sent in submit request; always send default (non-installment) object
        // paymentTerms: formData.paymentMethod === 'installment' ? (() => {
        //   // If we have Excel installments, use actual values from the table
        //   if (editableInstallments.length > 0 && excelFile) {
        //     const downPaymentInst = editableInstallments.find(inst =>
        //       inst.number.toLowerCase().includes('down')
        //     ) || editableInstallments[0];
        //
        //     const quarterlyInst = editableInstallments.find(inst =>
        //       !inst.number.toLowerCase().includes('down') &&
        //       (inst.number.toLowerCase().includes('q1') || inst.number.toLowerCase().includes('installment'))
        //     ) || editableInstallments.find(inst => !inst.number.toLowerCase().includes('down')) || editableInstallments[1];
        //
        //     const downPaymentAmount = downPaymentInst
        //       ? (downPaymentInst.rawAmount !== undefined
        //           ? downPaymentInst.rawAmount
        //           : (parseFloat(downPaymentInst.amount?.toString().replace(/[^\d.-]/g, '') || '0') || 0))
        //       : paymentTerms.downPayment;
        //
        //     const quarterlyAmount = quarterlyInst
        //       ? (quarterlyInst.rawAmount !== undefined
        //           ? quarterlyInst.rawAmount
        //           : (parseFloat(quarterlyInst.amount?.toString().replace(/[^\d.-]/g, '') || '0') || 0))
        //       : paymentTerms.quarterlyInstallment;
        //
        //     const totalInstallmentsCount = editableInstallments.filter(inst =>
        //       !inst.number.toLowerCase().includes('down')
        //     ).length;
        //
        //     const totalPrice = downPaymentAmount + (quarterlyAmount * totalInstallmentsCount);
        //
        //     return {
        //       totalPrice: totalPrice,
        //       downPayment: downPaymentAmount,
        //       quarterlyInstallment: quarterlyAmount,
        //       tenorMonths: totalInstallmentsCount * 3,
        //       totalInstallments: totalInstallmentsCount,
        //       downPaymentPercent: totalPrice > 0 ? downPaymentAmount / totalPrice : paymentTerms.downPaymentPercent
        //     };
        //   }
        //
        //   // Otherwise use calculated payment terms
        //   return {
        //     totalPrice: paymentTerms.price,
        //     downPayment: paymentTerms.downPayment,
        //     quarterlyInstallment: paymentTerms.quarterlyInstallment,
        //     tenorMonths: paymentTerms.tenorMonths,
        //     totalInstallments: paymentTerms.totalInstallments,
        //     downPaymentPercent: paymentTerms.downPaymentPercent
        //   };
        // })() : {
        //   totalPrice: paymentTerms.price,
        //   downPayment: paymentTerms.price,
        //   quarterlyInstallment: 0,
        //   tenorMonths: 0,
        //   totalInstallments: 0,
        //   downPaymentPercent: 1
        // },
        // NPM/Najma: PaymentMethod and PackageType at root (IsEgyptian is per CoOwner)
        ...((config.isNpm || config.isNajma) && {
          PaymentMethod: formData.paymentMethod
            ? formData.paymentMethod.charAt(0).toUpperCase() +
              formData.paymentMethod.slice(1).toLowerCase()
            : null,
          PackageType: (formData.packageType || "silver").charAt(0).toUpperCase() +
            (formData.packageType || "silver").slice(1).toLowerCase(),
        }),
        ...(nextForm.bankAccount && { BankAccount: nextForm.bankAccount }),
        ...(nextForm.bankName && { BankName: nextForm.bankName }),
        ...(nextForm.bankBranch && { BankBranch: nextForm.bankBranch }),
        ...(nextForm.swiftCode && { SwiftCode: nextForm.swiftCode }),
      };

      const reservationResult =
        await submitEnhancedReservation(reservationData);

      if (reservationResult.id) {
        const files = {
          idFront: nextForm.idFront,
          idBack: nextForm.idBack,
          other: Array.isArray(nextForm.other)
            ? nextForm.other
            : nextForm.other
              ? [nextForm.other]
              : [],
        };

        if (
          files.idFront ||
          files.idBack ||
          (files.other && files.other.length > 0)
        ) {
          try {
            const reservationId =
              reservationResult.data?.reservationId ||
              reservationResult.data?.id ||
              reservationResult.id ||
              1;
            console.log("Uploading files for reservation ID:", reservationId);
            console.log("Files to upload:", files);
            await uploadReservationFiles(reservationId, files);
            setSnackbar({
              open: true,
              message: "Reservation and files submitted successfully!",
              severity: "success",
            });
            // Clear upload failure state on success
            setUploadFailed(false);
            setPendingReservationId(null);
            setPendingFiles(null);
            setTimeout(() => {
              navigate(returnPath, { replace: true });
            }, 2000);
          } catch (fileError) {
            console.error("File upload error:", fileError);
            const reservationId =
              reservationResult.data?.reservationId ||
              reservationResult.data?.id ||
              reservationResult.id ||
              1;
            // Store reservation ID and files for retry
            setPendingReservationId(reservationId);
            setPendingFiles(files);
            setUploadFailed(true);
            setSnackbar({
              open: true,
              message:
                "Reservation submitted but file upload failed. Please try reuploading the files.",
              severity: "warning",
            });
            // Don't reset form - allow user to retry upload
          }
        } else {
          setSnackbar({
            open: true,
            message: "Reservation submitted successfully!",
            severity: "success",
          });
          // Clear upload failure state
          setUploadFailed(false);
          setPendingReservationId(null);
          setPendingFiles(null);
          setTimeout(() => {
            navigate(returnPath, { replace: true });
          }, 2000);
        }
      } else {
        setSnackbar({
          open: true,
          message:
            reservationResult.data?.message || "Failed to submit reservation.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Reservation submission error:", error);
      // Request.js rejects with error.response.data (a plain object {message:"..."})
      // so check error.message first, then fallback chains
      const errorMessage =
        error?.message ||
        error?.response?.data?.message ||
        (typeof error === "string" ? error : null) ||
        "An unexpected error occurred.";
      setSnackbar({ open: true, message: errorMessage, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  if (!unit)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#f8f9fa",
        py: 4,
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          background: "white",
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
          overflow: "hidden",
          p: { xs: 2, md: 3 },
        }}
      >
        {!showNextForm ? (
          <>
            {/* Header Section */}
            <Box
              sx={{
                background: "#465064",
                color: "white",
                p: 4,
                textAlign: "center",
                borderRadius: "12px",
                mb: 3,
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                }}
              >
                💳 Unit Payment
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
                Complete your reservation process
              </Typography>
            </Box>

            <Box sx={{ p: { xs: 1, md: 2 } }}>
              {/* Unit Card with Enhanced Styling */}
              <Card
                sx={{
                  mb: 4,
                  borderRadius: 2,
                  boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
                  border: "1px solid #e9ecef",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <UnitCard unit={unit} clickable={false} />
                </CardContent>
              </Card>

              {/* PDF Files Display Section */}
              {unit &&
                (unit.files || unit.Files) &&
                (unit.files || unit.Files).length > 0 && (
                  <Card
                    sx={{
                      mb: 4,
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
                      border: "1px solid #e9ecef",
                      width: "100%",
                    }}
                  >
                    <CardContent sx={{ p: 3, width: "100%" }}>
                      {/* <Typography variant="h5" sx={{ 
                      fontWeight: 600, 
                      mb: 3,
                      color: '#495057',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      pb: 2,
                      borderBottom: '2px solid #e9ecef',
                      width: '100%'
                    }}>
                      📄 Unit Documents
                    </Typography>
                    <Grid container spacing={3}> */}
                      {(unit.files || unit.Files).map((file, index) => {
                        // file may be a JSON-encoded array string like '["/pdfs/LG-D-13.pdf"]'
                        let filePath = file;
                        try {
                          const parsed = JSON.parse(file);
                          if (Array.isArray(parsed) && parsed.length > 0) filePath = parsed[0];
                        } catch (_) {}
                        // Check if file is a PDF
                        const isPdf =
                          filePath.toLowerCase().includes(".pdf") ||
                          filePath.toLowerCase().includes("pdf");
                        if (isPdf) {
                          return (
                            <Grid item xs={12} key={index}>
                              <Paper
                                elevation={2}
                                sx={{ p: 2, borderRadius: 2 }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{ mb: 2, fontWeight: 500 }}
                                >
                                  📄{" "}
                                  {filePath.split("/").pop() ||
                                    `Document ${index + 1}`}
                                </Typography>
                                <PDFViewer pdfUrl={ImageBaseUrl + filePath} />
                              </Paper>
                            </Grid>
                          );
                        }
                        return null;
                      })}
                      {/* </Grid> */}
                    </CardContent>
                  </Card>
                )}

              {/* Package Type Selector */}
              {(
                <Card
                  sx={{
                    mb: 4,
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
                    border: "1px solid #e9ecef",
                    background: "#f8f9fa",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        mb: 3,
                        color: "#465064",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      ⭐ Package Type
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item size={{ xs: 12, md: 12, lg: 12 }}>
                        <TextField
                          select
                          variant="outlined"
                          label="Select Package Type"
                          name="packageType"
                          value={formData.packageType}
                          onChange={handleInputChange}
                          fullWidth
                          required
                          error={!!formErrors.packageType}
                          helperText={formErrors.packageType}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              backgroundColor: "white",
                            },
                          }}
                        >
                          <MenuItem value="golden">Golden</MenuItem>
                          <MenuItem value="silver">Silver</MenuItem>
                        </TextField>
                      </Grid>
                      {formData.packageType && unit && (
                        <Grid item xs={12} md={6}>
                          <Card
                            sx={{
                              p: 2,
                              background:
                                formData.packageType === "golden"
                                  ? "linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)"
                                  : "linear-gradient(135deg, #c0c0c0 0%, #e8e8e8 100%)",
                              borderRadius: 2,
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  color:
                                    formData.packageType === "golden"
                                      ? "#856404"
                                      : "#495057",
                                  fontWeight: 500,
                                  mb: 0.5,
                                }}
                              >
                                {formData.packageType === "golden"
                                  ? "🏆"
                                  : "🥈"}{" "}
                                {formData.packageType.charAt(0).toUpperCase() +
                                  formData.packageType.slice(1)}{" "}
                                Package Price
                              </Typography>
                              <Typography
                                variant="h5"
                                sx={{
                                  color:
                                    formData.packageType === "golden"
                                      ? "#856404"
                                      : "#495057",
                                  fontWeight: 700,
                                }}
                              >
                                {paymentTerms.price.toLocaleString()} EGP
                              </Typography>
                            </Box>
                          </Card>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Payment Method Selector - NPM/Najma Environment Only */}
              {(config.isNpm || config.isNajma) &&
                (config.isNajma || formData.packageType) && (
                  <Card
                    sx={{
                      mb: 4,
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
                      border: "1px solid #e9ecef",
                      background: "#f8f9fa",
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          mb: 3,
                          color: "#465064",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        💳 Payment Method
                      </Typography>
                      <TextField
                        select
                        variant="outlined"
                        label="Select Payment Method"
                        name="paymentMethod"
                        value={formData.paymentMethod}
                        onChange={handleInputChange}
                        fullWidth
                        required
                        error={!!formErrors.paymentMethod}
                        helperText={formErrors.paymentMethod}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            backgroundColor: "white",
                          },
                        }}
                      >
                        <MenuItem value="cash">Cash</MenuItem>
                        <MenuItem value="installment">Installment</MenuItem>
                      </TextField>
                    </CardContent>
                  </Card>
                )}

              {/* Feedback - Global for both Cash and Installments (NPM/Najma environments) */}
              {(config.isNpm || config.isNajma) && formData.paymentMethod && (
                <Card
                  sx={{
                    mb: 4,
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
                    border: "1px solid #e9ecef",
                    background: "#f8f9fa",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        color: "#465064",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      💬 Feedback
                    </Typography>
                    <TextField
                      fullWidth
                      label="Share your thoughts..."
                      name="feedback"
                      multiline
                      rows={3}
                      value={formData.feedback}
                      onChange={handleInputChange}
                      disabled={loading}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          backgroundColor: "white",
                        },
                      }}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Feedback - For non-NPM/Najma environments (always shown) */}
              {!(config.isNpm || config.isNajma) && (
                <Card
                  sx={{
                    mb: 4,
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
                    border: "1px solid #e9ecef",
                    background: "#f8f9fa",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        color: "#465064",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      💬 Feedback
                    </Typography>
                    <TextField
                      fullWidth
                      label="Share your thoughts..."
                      name="feedback"
                      multiline
                      rows={3}
                      value={formData.feedback}
                      onChange={handleInputChange}
                      disabled={loading}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          backgroundColor: "white",
                        },
                      }}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Only show PaymentDetailsForm when NOT NPM/Najma (NPM/Najma: user only selects cash/installment and sees gold/silver price) */}
              {!(config.isNpm || config.isNajma) && (
                <PaymentDetailsForm
                  paymentTerms={paymentTerms}
                  installmentYears={installmentYears}
                  setInstallmentYears={setInstallmentYears}
                  formData={formData}
                  handleInputChange={handleInputChange}
                  firstPaymentDate={firstPaymentDate}
                  setFirstPaymentDate={setFirstPaymentDate}
                  installments={installments}
                  loading={loading}
                  buttonText="Continue to Personal Information"
                  onExcelUpload={handleExcelUpload}
                  excelFile={excelFile}
                  excelParsingError={excelParsingError}
                  onInstallmentUpdate={handleInstallmentUpdate}
                  maintenanceInstallments={editableMaintenanceInstallments}
                  onMaintenanceInstallmentUpdate={
                    handleMaintenanceInstallmentUpdate
                  }
                  onButtonClick={() => {
                    // Validate first form fields before proceeding
                    const firstFormErrors = {};

                    firstFormErrors.packageType = validateField("packageType", formData.packageType);
                    if (config.isNpm || config.isNajma) {
                      firstFormErrors.paymentMethod = validateField("paymentMethod", formData.paymentMethod);
                    }

                    setFormErrors((prev) => ({ ...prev, ...firstFormErrors }));

                    // Check if there are any errors
                    const hasErrors = Object.values(firstFormErrors).some(
                      (error) => error,
                    );

                    if (!hasErrors) {
                      // For NPM/Najma environment, copy packageType and paymentMethod to nextForm for submission
                      if (config.isNpm || config.isNajma) {
                        setNextForm((prev) => ({
                          ...prev,
                          packageType: formData.packageType,
                          paymentMethod: formData.paymentMethod,
                        }));
                      }
                      setShowNextForm(true);
                    } else {
                      setSnackbar({
                        open: true,
                        message:
                          "Please fix the validation errors before continuing.",
                        severity: "error",
                      });
                    }
                  }}
                  showInstallmentSchedule={true}
                  formErrors={formErrors}
                  handleFirstPaymentDateChange={handleFirstPaymentDateChange}
                />
              )}

              {/* NPM/Najma: Payment form for both Cash and Installment (title, bank details, switch + continue) */}
              {(config.isNpm || config.isNajma) &&
                (formData.paymentMethod === "cash" ||
                  formData.paymentMethod === "installment") && (
                  <Card
                    sx={{
                      mb: 4,
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
                      border: "1px solid #e9ecef",
                      ...(formData.paymentMethod === "cash" && {
                        background: "#fff3cd",
                        borderColor: "#ffc107",
                      }),
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          color:
                            formData.paymentMethod === "cash"
                              ? "#856404"
                              : "#495057",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        {formData.paymentMethod === "cash"
                          ? "💰 Cash Payment Selected"
                          : "📋 Installment Payment Selected"}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color:
                            formData.paymentMethod === "cash"
                              ? "#856404"
                              : "#495057",
                          mb: 3,
                        }}
                      >
                        {formData.paymentMethod === "cash"
                          ? "You have selected cash payment."
                          : "You have selected installment payment."}
                      </Typography>

                      {/* Bank Account Details (Optional) - shown for both cash and installment */}
                      <Box
                        sx={{
                          mb: 3,
                          p: 2,
                          background: "white",
                          borderRadius: 2,
                          border:
                            formData.paymentMethod === "cash"
                              ? "1px solid #ffc107"
                              : "1px solid #e9ecef",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            mb: 2,
                            color:
                              formData.paymentMethod === "cash"
                                ? "#856404"
                                : "#495057",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          🏦 Bank Account Details (Optional)
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item size={{ xs: 12, md: 6, lg: 6 }}>
                            <TextField
                              variant="outlined"
                              label="Bank Account Number"
                              name="bankAccount"
                              value={nextForm.bankAccount}
                              onChange={handleNextFormChange}
                              fullWidth
                              placeholder="Enter bank account number"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                  backgroundColor: "white",
                                },
                              }}
                            />
                          </Grid>
                          <Grid item size={{ xs: 12, md: 6, lg: 6 }}>
                            <Autocomplete
                              freeSolo
                              options={banks.map((bank) => bank.name)}
                              loading={banksLoading}
                              value={nextForm.bankName}
                              onInputChange={(event, newValue) => {
                                setNextForm((prev) => ({
                                  ...prev,
                                  bankName: newValue || "",
                                }));
                                if (newValue && newValue.length >= 2) {
                                  fetchBanks(newValue);
                                } else {
                                  setBanks([]);
                                }
                              }}
                              onChange={(event, newValue) => {
                                setNextForm((prev) => ({
                                  ...prev,
                                  bankName: newValue || "",
                                }));
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="outlined"
                                  label="Bank Name"
                                  name="bankName"
                                  placeholder="Type to search banks..."
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 2,
                                      backgroundColor: "white",
                                    },
                                  }}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item size={{ xs: 12, md: 6, lg: 6 }}>
                            <TextField
                              variant="outlined"
                              label="Bank Branch"
                              name="bankBranch"
                              value={nextForm.bankBranch}
                              onChange={handleNextFormChange}
                              fullWidth
                              placeholder="Enter bank branch"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                  backgroundColor: "white",
                                },
                              }}
                            />
                          </Grid>
                          <Grid item size={{ xs: 12, md: 6, lg: 6 }}>
                            <TextField
                              variant="outlined"
                              label="SWIFT Code"
                              name="swiftCode"
                              value={nextForm.swiftCode}
                              onChange={handleNextFormChange}
                              fullWidth
                              placeholder="Enter SWIFT/BIC code"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                  backgroundColor: "white",
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Box>

                      <Box sx={{ display: "flex", gap: 2 }}>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              paymentMethod:
                                formData.paymentMethod === "cash"
                                  ? "installment"
                                  : "cash",
                            }));
                            setFormErrors((prev) => ({
                              ...prev,
                              paymentMethod: "",
                            }));
                          }}
                          sx={{
                            ...(formData.paymentMethod === "cash"
                              ? {
                                  borderColor: "#856404",
                                  color: "#856404",
                                  "&:hover": {
                                    borderColor: "#856404",
                                    backgroundColor: "rgba(133, 100, 4, 0.1)",
                                  },
                                }
                              : {
                                  borderColor: "#465064",
                                  color: "#465064",
                                  "&:hover": {
                                    borderColor: "#465064",
                                    backgroundColor: "rgba(70, 80, 100, 0.08)",
                                  },
                                }),
                          }}
                        >
                          {formData.paymentMethod === "cash"
                            ? "Switch to Installment"
                            : "Switch to Cash"}
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() => {
                            const firstFormErrors = {};
                            firstFormErrors.packageType = validateField("packageType", formData.packageType);
                            firstFormErrors.paymentMethod = validateField(
                              "paymentMethod",
                              formData.paymentMethod,
                            );
                            setFormErrors((prev) => ({
                              ...prev,
                              ...firstFormErrors,
                            }));

                            const hasErrors = Object.values(
                              firstFormErrors,
                            ).some((error) => error);

                            if (!hasErrors) {
                              setNextForm((prev) => ({
                                ...prev,
                                packageType: formData.packageType,
                                paymentMethod: formData.paymentMethod,
                              }));
                              setShowNextForm(true);
                            } else {
                              setSnackbar({
                                open: true,
                                message:
                                  "Please fix the validation errors before continuing.",
                                severity: "error",
                              });
                            }
                          }}
                          sx={{
                            background: "#465064",
                            "&:hover": { background: "#3a4354" },
                          }}
                        >
                          Continue to Personal Information
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                )}
            </Box>
          </>
        ) : (
          <>
            {/* Personal Information Form */}
            <Box
              sx={{
                background: "#465064",
                color: "white",
                p: 4,
                textAlign: "center",
                borderRadius: "12px",
                mb: 3,
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                }}
              >
                👤 Personal & Broker Information
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
                Complete your reservation details
              </Typography>
            </Box>

            <Box sx={{ p: { xs: 1, md: 2 } }}>
              <Card
                sx={{
                  borderRadius: 2,
                  background: "white",
                  boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
                  border: "1px solid #e9ecef",
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <PersonalInfoOwnershipForm
                    value={nextForm}
                    errors={formErrors}
                    onChange={handleNextFormChange}
                    coOwners={coOwners}
                    coOwnerErrors={coOwnerErrors}
                    onUpdateCoOwner={updateCoOwner}
                    onAddCoOwner={addCoOwner}
                    onRemoveCoOwner={removeCoOwner}
                    totalOwnershipPercent={getTotalOwnershipPercentage()}
                    ownershipError={
                      formErrors.coOwnerEmails ||
                      (() => {
                        const total = getTotalOwnershipPercentage();
                        const hasAny =
                          (parseFloat(nextForm.primaryOwnerPercentage) || 0) >
                            0 ||
                          coOwners.some(
                            (o) => (parseFloat(o.percentage) || 0) > 0,
                          );
                        return hasAny && Math.abs(total - 100) > 0.01
                          ? "Total must be 100%"
                          : "";
                      })() ||
                      formErrors.ownershipPercentages ||
                      ""
                    }
                  />

                  {/* File Upload Section */}
                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 3,
                        color: "#495057",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      📁 Required Documents
                    </Typography>
                    <Grid container spacing={3}>
                      {[
                        { key: "idFront", label: "ID Front Side", icon: "🆔" },
                        { key: "idBack", label: "ID Back Side", icon: "🆔" },
                        {
                          key: "other",
                          label: "Other Documents",
                          icon: "📄",
                          multiple: true,
                        },
                      ].map((file) => (
                        <Grid item xs={12} sm={4} key={file.key}>
                          <Button
                            variant="outlined"
                            component="label"
                            fullWidth
                            sx={{
                              py: 3,
                              borderRadius: 2,
                              borderColor: "#465064",
                              color: "#465064",
                              "&:hover": {
                                borderColor: "#465064",
                                backgroundColor: "rgba(70, 80, 100, 0.1)",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <span style={{ fontSize: "2rem" }}>
                                {file.icon}
                              </span>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {file.label}
                              </Typography>
                              {file.multiple && (
                                <Typography
                                  variant="caption"
                                  sx={{ fontSize: "0.7rem", opacity: 0.7 }}
                                >
                                  (Multiple files allowed)
                                </Typography>
                              )}
                            </Box>
                            <input
                              type="file"
                              name={file.key}
                              hidden
                              ref={fileInputRefs[file.key]}
                              onChange={handleNextFormChange}
                              multiple={file.multiple || false}
                              accept={file.multiple ? "*" : undefined}
                            />
                          </Button>
                          {/* Display single file for idFront and idBack */}
                          {!file.multiple && nextForm[file.key] && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mt: 1,
                                p: 1,
                                background: "#f8f9fa",
                                borderRadius: 1,
                              }}
                            >
                              <Typography variant="caption" sx={{ flex: 1 }}>
                                {nextForm[file.key].name}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleClearFile(file.key)}
                                sx={{ color: "red" }}
                              >
                                <CloseIcon />
                              </IconButton>
                            </Box>
                          )}
                          {/* Display multiple files for 'other' */}
                          {file.multiple &&
                            Array.isArray(nextForm[file.key]) &&
                            nextForm[file.key].length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                {nextForm[file.key].map((fileItem, index) => (
                                  <Box
                                    key={index}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      mt: 1,
                                      p: 1,
                                      background: "#f8f9fa",
                                      borderRadius: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{ flex: 1 }}
                                    >
                                      {fileItem.name}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleClearFile(file.key, index)
                                      }
                                      sx={{ color: "red" }}
                                    >
                                      <CloseIcon />
                                    </IconButton>
                                  </Box>
                                ))}
                                {nextForm[file.key].length > 0 && (
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => handleClearFile(file.key)}
                                    sx={{
                                      mt: 1,
                                      color: "red",
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    Clear All
                                  </Button>
                                )}
                              </Box>
                            )}
                        </Grid>
                      ))}
                    </Grid>
                  </Box>

                  {/* Broker Information Section */}
                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        mb: 3,
                        color: "#495057",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        pb: 2,
                        borderBottom: "2px solid #e9ecef",
                      }}
                    >
                      🏢 Broker Information
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item size={{ xs: 12, md: 6 }}>
                        <TextField
                          select
                          variant="outlined"
                          label="Broker"
                          name="brokerId"
                          value={nextForm.brokerId}
                          onChange={handleNextFormChange}
                          fullWidth
                          disabled={brokersLoading}
                          helperText={
                            brokersLoading
                              ? "Loading brokers..."
                              : selectedBroker
                                ? `Company: ${selectedBroker.Company || "N/A"}`
                                : ""
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        >
                          <MenuItem value="">
                            {brokersLoading
                              ? "Loading brokers..."
                              : "Select Broker"}
                          </MenuItem>
                          {brokers.map((broker) => (
                            <MenuItem key={broker.Id} value={broker.Id}>
                              {broker.Name}
                              {broker.Company ? ` - ${broker.Company}` : ""}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      {selectedBroker && (
                        <Grid item xs={12} sm={6}>
                          <Box
                            sx={{
                              border: "1px solid #e9ecef",
                              borderRadius: 2,
                              p: 2,
                              height: "100%",
                              backgroundColor: "#f8f9fa",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ color: "#495057", mb: 1 }}
                            >
                              Selected Broker Details
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {selectedBroker.Name}
                            </Typography>
                            {selectedBroker.Company && (
                              <Typography
                                variant="body2"
                                sx={{ color: "#6c757d" }}
                              >
                                {selectedBroker.Company}
                              </Typography>
                            )}
                            {selectedBroker.Email && (
                              <Typography
                                variant="body2"
                                sx={{ mt: 1, color: "#6c757d" }}
                              >
                                Email: {selectedBroker.Email}
                              </Typography>
                            )}
                            {selectedBroker.PhoneNumber && (
                              <Typography
                                variant="body2"
                                sx={{ color: "#6c757d" }}
                              >
                                Phone: {selectedBroker.PhoneNumber}
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      )}
                      <Grid item size={{ xs: 12, md: 6 }}>
                        <TextField
                          variant="outlined"
                          label="Direct Manager"
                          name="directManager"
                          value={nextForm.directManager}
                          onChange={handleNextFormChange}
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item size={{ xs: 12, md: 6 }}>
                        <TextField
                          variant="outlined"
                          label="Developer Sales"
                          name="developerSales"
                          value={nextForm.developerSales}
                          onChange={handleNextFormChange}
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item size={{ xs: 12, md: 6 }}>
                        <TextField
                          variant="outlined"
                          label="Developer Company"
                          name="developerCompany"
                          value={nextForm.developerCompany}
                          onChange={handleNextFormChange}
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item size={{ xs: 12, md: 6 }}>
                        <TextField
                          variant="outlined"
                          label="Developer Team Leader"
                          name="developerTeamLeader"
                          value={nextForm.developerTeamLeader}
                          onChange={handleNextFormChange}
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item size={{ xs: 12, md: 6 }}>
                        <TextField
                          variant="outlined"
                          label="Broker Team Leader"
                          name="brokerTeamLeader"
                          value={nextForm.brokerTeamLeader}
                          onChange={handleNextFormChange}
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item size={{ xs: 12, md: 6 }}>
                        <TextField
                          variant="outlined"
                          label="Company"
                          name="company"
                          value={nextForm.company}
                          onChange={handleNextFormChange}
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Upload Failed Alert */}
                  {uploadFailed && (
                    <Box
                      sx={{
                        mb: 3,
                        p: 2,
                        background: "#fff3cd",
                        borderRadius: 2,
                        border: "1px solid #ffc107",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ color: "#856404", mb: 2 }}
                      >
                        ⚠️ File upload failed. Your reservation was created
                        successfully, but the files could not be uploaded.
                        Please try reuploading the files.
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={handleReuploadFiles}
                        disabled={loading}
                        sx={{
                          background: "#ffc107",
                          color: "#856404",
                          fontWeight: 600,
                          "&:hover": {
                            background: "#ffb300",
                          },
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          "🔄 Reupload Files"
                        )}
                      </Button>
                    </Box>
                  )}

                  {/* Action Buttons */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 2,
                      pt: 3,
                      borderTop: "2px solid #e9ecef",
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => setShowNextForm(false)}
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontWeight: 600,
                        borderRadius: 2,
                        borderColor: "#465064",
                        color: "#465064",
                        "&:hover": {
                          borderColor: "#465064",
                          backgroundColor: "rgba(70, 80, 100, 0.1)",
                        },
                      }}
                    >
                      ← Back
                    </Button>
                    {view === "true" || uploadFailed ? (
                      <></>
                    ) : (
                      <>
                        <Button
                          variant="outlined"
                          onClick={resetForm}
                          disabled={loading}
                          sx={{
                            px: 4,
                            py: 1.5,
                            fontWeight: 600,
                            borderRadius: 2,
                            borderColor: "#465064",
                            color: "#465064",
                            "&:hover": {
                              borderColor: "#3a4354",
                              bgcolor: "rgba(70, 80, 100, 0.08)",
                            },
                          }}
                        >
                          Reset Form
                        </Button>
                        <Button
                          variant="contained"
                          sx={{
                            px: 6,
                            py: 1.5,
                            fontWeight: 600,
                            borderRadius: 2,
                            background: "#465064",
                            boxShadow: "0 2px 8px rgba(70, 80, 100, 0.15)",
                            "&:hover": {
                              background: "#3a4354",
                              boxShadow: "0 4px 12px rgba(70, 80, 100, 0.2)",
                              transform: "translateY(-2px)",
                            },
                            transition: "all 0.2s ease",
                          }}
                          onClick={handleSubmit}
                          disabled={loading}
                        >
                          {loading ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : (
                            "✅ Submit Reservation"
                          )}
                        </Button>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </>
        )}
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UnitPayment;
